import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http, money } from "../lib/api";
import { useCart } from "../lib/ctx";
import ProductCard, { Placeholder } from "../components/ProductCard";
import InterestForm from "../components/InterestForm";
import SEO, { productJsonLd } from "../components/SEO";
import { toast } from "sonner";
import { Search, Filter, Minus, Plus, MapPin, Instagram } from "lucide-react";

/* --------- Shop / category pages --------- */
export function ShopPage({ category }) {
  const [products, setProducts] = useState([]);
  const [promo, setPromo] = useState({ percent: 10, active: true });
  const [q, setQ] = useState("");
  const [maxPrice, setMaxPrice] = useState(50);
  const [inStock, setInStock] = useState(false);
  useEffect(() => {
    const params = category ? { params: { category } } : {};
    http.get("/products", params).then((r) => setProducts(r.data));
    http.get("/promo/current").then((r) => setPromo(r.data));
  }, [category]);
  const filtered = useMemo(() => products.filter((p) => {
    if (p.status !== "active") return false;
    if (q && !`${p.name} ${p.description} ${p.honey_variety||''} ${p.supplement_type||''}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (p.price > maxPrice) return false;
    if (inStock && (p.available_stock || 0) <= 0) return false;
    return true;
  }), [products, q, maxPrice, inStock]);
  const title = category === "honey" ? "Premium Honey" : category === "supplements" ? "Natural Supplements" : category === "herbal" ? "Herbal Products" : "Shop All";
  const seoTitle = category === "honey"
    ? "Premium Honey UK | Sidr, Acacia, Linden | Organic Healing"
    : category === "supplements"
    ? "Natural Supplements UK | Shilajit, Lion's Mane, Ashwagandha | Organic Healing"
    : "Shop All Products | Organic Healing UK";
  const seoDesc = category === "honey"
    ? "Shop premium raw honey — Yemeni Sidr, Spanish Sidr, Acacia and Linden. UK delivery and Birmingham collection available."
    : category === "supplements"
    ? "Shop natural food supplements — Siberian & Himalayan Shilajit, Lion's Mane, Ashwagandha, Bee Pollen and more. Secure UK checkout."
    : "Shop premium honey, natural food supplements and botanicals from Organic Healing UK. Secure checkout, UK delivery and collection available.";
  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
      <SEO title={seoTitle} description={seoDesc} path={category ? `/${category}` : "/shop"} />
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <span className="oh-badge oh-badge-turq">{filtered.length} products</span>
          <h1 className="font-serif text-5xl mt-3" data-testid="shop-title">{title}</h1>
        </div>
      </div>
      <div className="grid lg:grid-cols-[260px,1fr] gap-8">
        <aside className="oh-card p-5 h-fit sticky top-32 space-y-6">
          <div>
            <label className="text-xs font-mono tracking-widest text-white/50 mb-2 block">SEARCH</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
              <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="e.g. shilajit, sidr" data-testid="shop-search" className="oh-input pl-9"/>
            </div>
          </div>
          <div>
            <label className="text-xs font-mono tracking-widest text-white/50 mb-2 block">MAX PRICE · {money(maxPrice)}</label>
            <input type="range" min={5} max={50} value={maxPrice} onChange={(e)=>setMaxPrice(+e.target.value)} className="w-full accent-lime-500" data-testid="shop-price"/>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={inStock} onChange={(e)=>setInStock(e.target.checked)} data-testid="shop-instock"/> In stock only
          </label>
        </aside>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((p) => <ProductCard key={p.id} product={p} promoPercent={promo.active ? promo.percent : 0}/>)}
          {filtered.length === 0 && <div className="col-span-full text-center py-20 text-white/60">No products match your filters.</div>}
        </div>
      </div>
    </div>
  );
}

/* --------- Product Detail --------- */
export function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [promo, setPromo] = useState({ percent: 10, active: true });
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0);
  const { add } = useCart();
  const nav = useNavigate();
  useEffect(() => {
    http.get(`/products/${slug}`).then((r) => setP(r.data));
    http.get("/promo/current").then((r) => setPromo(r.data));
  }, [slug]);
  useEffect(() => {
    if (!p) return;
    http.get("/products", { params: { category: p.category } }).then((r) => setRelated(r.data.filter((x) => x.slug !== slug).slice(0, 4)));
    http.get("/reviews", { params: { product_id: p.id } }).then((r) => setReviews(r.data));
  }, [p, slug]);
  if (!p) return <div className="max-w-7xl mx-auto p-10 text-white/60">Loading…</div>;
  const original = p.price;
  const price = p.discount_price ?? (promo.active && promo.percent && p.status !== "draft" ? +(original * (1 - promo.percent/100)).toFixed(2) : original);
  const inStock = (p.available_stock||0) > 0;
  const isHoney = p.category === "honey";
  const seoTitle = `${p.name} | ${isHoney ? "Premium Honey" : "Natural Supplements"} | Organic Healing UK`;
  const seoDesc = p.short_description ? `${p.short_description} Shop with Organic Healing UK — secure checkout, UK delivery and collection available.` : `Shop ${p.name} from Organic Healing UK. Secure checkout, UK delivery and collection available.`;
  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={`/product/${p.slug}`}
        image={p.images?.[0]}
        jsonLd={productJsonLd(p, price)}
      />
      <div className="text-xs font-mono text-white/50 mb-6 tracking-widest">
        <Link to="/shop" className="link-hover">SHOP</Link> / <Link to={`/${p.category}`} className="link-hover">{p.category.toUpperCase()}</Link> / {p.name}
      </div>
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="oh-card rounded-2xl overflow-hidden">
            {p.images && p.images[img] ? <img src={p.images[img]} alt={p.name} className="w-full aspect-square object-cover"/> : <Placeholder category={p.category}/>}
          </div>
          {p.images && p.images.length > 1 && (
            <div className="flex gap-2 mt-3">
              {p.images.map((src, i) => <button key={i} onClick={()=>setImg(i)} className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${i===img?"border-lime-400":"border-transparent"}`}><img src={src} alt="" className="w-full h-full object-cover"/></button>)}
            </div>
          )}
        </div>
        <div className="space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`oh-badge ${isHoney?"oh-badge-gold":"oh-badge-turq"}`}>{p.category}</span>
            {p.honey_variety && <span className="oh-badge oh-badge-lime">{p.honey_variety}</span>}
            {p.suitable_for?.map((s) => <span key={s} className="oh-badge oh-badge-lime">{s}</span>)}
          </div>
          <h1 className="font-serif text-5xl" data-testid="pd-title">{p.name}</h1>
          <p className="text-white/70">{p.short_description}</p>
          {p.status === "coming_soon" && (
            <div className="oh-card p-5 border border-lime-500/40 bg-lime-500/5">
              <InterestForm product={p}/>
            </div>
          )}
          {p.status !== "coming_soon" && (
            <>
              <div className="flex items-baseline gap-3">
                {price !== original && original > 0 && <div className="strike text-lg">{money(original)}</div>}
                <div className={`font-mono text-3xl ${isHoney?"text-amber-300":"text-lime-300"}`} data-testid="pd-price">{original > 0 ? money(price) : "Set by admin"}</div>
                {price !== original && <span className="oh-badge oh-badge-lime">SAVE {Math.round((1 - price/original)*100)}%</span>}
              </div>
              <div className="text-xs font-mono text-white/60 tracking-widest">{p.net_quantity} · {inStock ? `${p.available_stock} in stock` : "Sold out"} · {p.country_of_origin || "—"}</div>
            </>
          )}
          {p.status === "coming_soon" && (
            <div className="text-xs font-mono text-white/60 tracking-widest">{p.net_quantity} · {p.country_of_origin || "—"}</div>
          )}
          {p.status !== "draft" && inStock && original > 0 && (
            <div className="flex items-center gap-3 pt-2">
              <div className="flex items-center border border-[var(--oh-border)] rounded-full overflow-hidden">
                <button className="px-3 py-2 hover:bg-white/5" data-testid="qty-minus" onClick={()=>setQty(Math.max(1,qty-1))}><Minus size={14}/></button>
                <span className="px-4 font-mono" data-testid="qty-value">{qty}</span>
                <button className="px-3 py-2 hover:bg-white/5" data-testid="qty-plus" onClick={()=>setQty(qty+1)}><Plus size={14}/></button>
              </div>
              <button data-testid="pd-add" onClick={()=>{ add({...p, discount_price: price !== original ? price : null}, qty); toast.success(`${qty} × ${p.name} added`); }} className="oh-btn-lime px-6 py-2.5 rounded-full text-xs tracking-widest uppercase">Add to Basket</button>
              <button data-testid="pd-buy" onClick={()=>{ add({...p, discount_price: price !== original ? price : null}, qty); nav("/basket"); }} className={`${isHoney?"oh-btn-gold":"oh-btn-ghost"} px-6 py-2.5 rounded-full text-xs tracking-widest uppercase`}>Buy Now</button>
            </div>
          )}
          <div className="pt-4 space-y-4 text-sm">
            {p.description && <Section title="Description">{p.description}</Section>}
            {p.ingredients && <Section title="Ingredients">{p.ingredients}</Section>}
            {p.allergens && <Section title="Allergens">{p.allergens}</Section>}
            {p.nutrition && <Section title="Nutrition">{p.nutrition}</Section>}
            {p.directions && <Section title="Directions">{p.directions}</Section>}
            {p.storage && <Section title="Storage">{p.storage}</Section>}
            {p.safety_warnings && <Section title="Safety">{p.safety_warnings}</Section>}
            <p className="text-xs text-white/40 italic border-t border-[var(--oh-border)] pt-4">Food supplement. Not intended to diagnose, treat, prevent or cure any disease. Keep out of reach of children.</p>
          </div>
        </div>
      </div>
      {reviews.length > 0 && (
        <div className="mt-16">
          <h2 className="font-serif text-3xl mb-6">Customer Reviews</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {reviews.map((r) => <div key={r.id} className="oh-card p-5"><div className="text-amber-400 mb-2">{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</div><h3 className="font-serif text-xl">{r.title}</h3><p className="text-sm text-white/70 mt-2">{r.body}</p><div className="text-xs mt-3 font-mono text-white/40">{r.user_name} {r.verified_purchase && "· Verified purchase"}</div></div>)}
          </div>
        </div>
      )}
      {related.length > 0 && (
        <div className="mt-16">
          <h2 className="font-serif text-3xl mb-6">You Might Also Like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((x) => <ProductCard key={x.id} product={x} promoPercent={promo.active ? promo.percent : 0}/>)}
          </div>
        </div>
      )}
    </div>
  );
}
function Section({ title, children }) { return <div><h3 className="font-mono text-xs tracking-widest text-lime-400 mb-1">{title.toUpperCase()}</h3><div className="text-white/80 whitespace-pre-line">{children}</div></div>; }

/* --------- Basket --------- */
export function Basket() {
  const { items, update, remove } = useCart();
  const [promo, setPromo] = useState({ percent: 10, active: true });
  useEffect(() => { http.get("/promo/current").then((r) => setPromo(r.data)); }, []);
  const subtotal = items.reduce((s,i)=>s + ((i.discount_price ?? (promo.active && promo.percent ? i.price*(1-promo.percent/100) : i.price)) * i.quantity), 0);
  const delivery = subtotal >= 40 ? 0 : 4.95;
  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-14">
      <h1 className="font-serif text-5xl mb-8" data-testid="basket-title">Your Basket</h1>
      {items.length === 0 ? (
        <div className="oh-card p-10 text-center">
          <p className="text-white/60 mb-4">Your basket is empty.</p>
          <Link to="/shop" className="oh-btn-lime px-6 py-2.5 rounded-full text-xs tracking-widest uppercase inline-block">Start Shopping</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr,380px] gap-8">
          <div className="space-y-3">
            {items.map((it) => {
              const price = it.discount_price ?? (promo.active && promo.percent ? +(it.price*(1-promo.percent/100)).toFixed(2) : it.price);
              return (
                <div key={it.product_id} data-testid={`basket-item-${it.slug}`} className="oh-card p-4 flex items-center gap-4">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-[var(--oh-surface)] shrink-0">{it.image ? <img src={it.image} alt="" className="w-full h-full object-cover"/> : <Placeholder/>}</div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg">{it.name}</h3>
                    <div className="text-xs font-mono text-white/50">{money(price)} each</div>
                  </div>
                  <div className="flex items-center border border-[var(--oh-border)] rounded-full">
                    <button className="px-2 py-1" data-testid={`dec-${it.slug}`} onClick={()=>update(it.product_id, it.quantity-1)}><Minus size={12}/></button>
                    <span className="px-3 text-sm font-mono">{it.quantity}</span>
                    <button className="px-2 py-1" data-testid={`inc-${it.slug}`} onClick={()=>update(it.product_id, it.quantity+1)}><Plus size={12}/></button>
                  </div>
                  <div className="text-right w-24 font-mono text-lime-300">{money(price*it.quantity)}</div>
                  <button className="text-white/40 hover:text-red-400 text-xs" data-testid={`rm-${it.slug}`} onClick={()=>remove(it.product_id)}>✕</button>
                </div>
              );
            })}
          </div>
          <aside className="oh-card p-6 h-fit sticky top-32 space-y-4">
            <h3 className="font-serif text-2xl">Summary</h3>
            <Row label="Subtotal" value={money(subtotal)}/>
            <Row label="Delivery (est.)" value={delivery === 0 ? "FREE" : money(delivery)}/>
            {promo.active && <div className="text-xs text-lime-400 font-mono tracking-widest">✓ 10% AUTO DISCOUNT APPLIED</div>}
            <div className="border-t border-[var(--oh-border)] pt-4 flex justify-between font-mono text-lg"><span>Total</span><span className="text-lime-300" data-testid="basket-total">{money(subtotal + delivery)}</span></div>
            <Link to="/checkout" data-testid="go-checkout" className="oh-btn-lime block text-center px-6 py-3 rounded-full text-xs tracking-widest uppercase">Proceed to Checkout</Link>
            <div className="text-xs text-white/50 text-center">Free UK delivery on orders over £40</div>
          </aside>
        </div>
      )}
    </div>
  );
}
function Row({ label, value }) { return <div className="flex justify-between text-sm text-white/75"><span>{label}</span><span>{value}</span></div>; }
